import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

const Hero = () => {
  const { t } = useTranslation();

  return (
    <section className="relative min-h-screen flex items-center bg-ivory overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-cream hidden lg:block"></div>
      <div className="absolute bottom-0 left-1/4 w-px h-32 bg-gradient-to-t from-gold to-transparent hidden lg:block"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Text content */}
          <div className="order-2 lg:order-1">
            <motion.p 
              className="eyebrow mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {t('hero.eyebrow')}
            </motion.p>
            
            <motion.h1 
              className="font-serif text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-charcoal mb-8 leading-[1.1]"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              {t('hero.title1')}<br />
              <span className="text-gold">{t('hero.title2')}</span><br />
              {t('hero.title3')}
            </motion.h1>
            
            <motion.div 
              className="w-16 h-px bg-gold mb-8"
              initial={{ width: 0 }}
              animate={{ width: 64 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            />
            
            <motion.p 
              className="text-text-secondary text-lg leading-relaxed mb-10 max-w-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              {t('hero.description')}
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              <Link 
                to="/contacto"
                className="btn-gold inline-flex items-center justify-center gap-2 group"
              >
                {t('hero.cta1')}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link 
                to="/el-doctor"
                className="btn-outline-gold inline-flex items-center justify-center"
              >
                {t('hero.cta2')}
              </Link>
            </motion.div>
          </div>
          
          {/* Image */}
          <motion.div 
            className="order-1 lg:order-2 relative"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="relative">
              {/* Gold frame accent */}
              <motion.div 
                className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-gold"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.8 }}
              />
              <motion.div 
                className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-gold"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.9 }}
              />
              
              <img 
                src="https://customer-assets.emergentagent.com/job_norro-rebranding/artifacts/ygavpxj3_w8t0nc1j_IMG_6797.jpg"
                alt="Dr. Ángel Norro y su equipo en quirófano"
                className="w-full h-[500px] lg:h-[600px] object-cover"
              />
              
              {/* Overlay info card */}
              <motion.div 
                className="absolute bottom-8 left-8 right-8 lg:left-auto lg:right-8 lg:w-72 bg-charcoal/95 backdrop-blur-sm p-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1 }}
              >
                <p className="text-gold text-xs tracking-[0.3em] uppercase mb-2">{t('hero.doctorName')}</p>
                <p className="text-ivory font-serif text-xl mb-2">{t('hero.doctorTitle')}</p>
                <p className="text-text-muted text-sm">{t('hero.doctorSubtitle')}</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Scroll indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-text-muted"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 1.2 }}
      >
        <span className="text-xs tracking-[0.3em] uppercase">{t('hero.scroll')}</span>
        <motion.div 
          className="w-px h-8 bg-gradient-to-b from-gold to-transparent"
          initial={{ height: 0 }}
          animate={{ height: 32 }}
          transition={{ duration: 0.8, delay: 1.4 }}
        />
      </motion.div>
    </section>
  );
};

export default Hero;
